import { useCallback, useState } from 'react';
import { useAuth } from '@/app/user-and-session/session-auth/hooks/useAuth';
import { useUserAppData } from '@/app/user-and-session/user-app-data/useUserAppData';
import { useLocale } from '@/app/user-and-session/languages/i18n/locale-provider';
import { getCurrentImageModelPrice } from '../data/chatSetting/settingOptions';
import { getPresignedUrl } from '@/app/lib/actions/s3';
import { useGlobalChatState, useGlobalLiveKitRoomState } from './useGlobalChatState';
import { ChatStatus } from '../data/globalChatState';
import type { Locale } from '@/app/api/chat/systemp_prompt/system_prompt';

interface ImageData {
  filename: string;
  base64: string;
  width: number;
  height: number;
}

interface ImageGenRequest {
  image1: ImageData;
  image2?: ImageData;
  prompt: string;
  source: string;
  currentGems: number;
  price: number;
}

interface ImageGenResponse {
  success: boolean;
  image?: string;
  jobId?: string;
  status?: string;
  executionTime?: number;
  delayTime?: number;
  error?: string;
}

interface UseImageGenAPIParams {
  onImageGenerated?: (imageBase64: string) => void;
}

/**
 * Hook for sending image generation requests to API backend
 * Replaces LiveKit-based communication with direct API calls
 */
export function useImageGenAPI({ onImageGenerated }: UseImageGenAPIParams = {}) {
  const { userEmail, isAuthenticated, isLoading: authLoading } = useAuth();
  const { locale } = useLocale();
  const [isGenerating, setIsGenerating] = useState(false);
  const [lastGeneratedImage, setLastGeneratedImage] = useState<string | null>(null);
  const [globalChatStateData, globalChatStateActions] = useGlobalChatState();

  // User app data hook for gem count (singleton) - only after auth loading completes
  const [userAppDataState, userAppDataActions] = useUserAppData(
    !authLoading && isAuthenticated && userEmail ? userEmail : ''
  );

  const generateImage = useCallback(async (
    imageData: ImageData,
    prompt: string,
    source?: string,
    imageData2?: ImageData
  ): Promise<ImageGenResponse> => {
    if (!userEmail) {
      return { success: false, error: 'authentication_required' };
    }

    if (!imageData || !prompt.trim()) {
      return { success: false, error: 'missing_required_fields' };
    }

    // Calculate price for image generation
    const currentModelPrice = getCurrentImageModelPrice(globalChatStateData.userChatSetting.imageModel);
    const currentGems = userAppDataState.creditCount + userAppDataState.dailyCreditCount;

    // Check if user has sufficient credits before sending request
    try {
      // Use singleton gem count for faster access
      if (currentGems < currentModelPrice) {
        return { success: false, error: 'insufficient_credits' };
      }
    } catch (error) {
      console.error('Error checking credits:', error);
      // If credit check fails, still allow sending request (fail-safe approach)
    }

    setIsGenerating(true);

    try {
      const apiPayload: ImageGenRequest = {
        image1: imageData,
        ...(imageData2 && { image2: imageData2 }),
        prompt: prompt.trim(),
        source: source || 'in-chat customize',
        currentGems: currentGems,
        price: currentModelPrice
      };

      console.log('Sending image generation request to API...');

      const response = await fetch('/api/chat/imagegen', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(apiPayload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `API call failed: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Image generation failed');
      }

      if (!data.image) {
        throw new Error('No image data received from server');
      }

      // Deduct credits after successful API call
      try {
        const currentModelPrice = getCurrentImageModelPrice(globalChatStateData.userChatSetting.imageModel);
        await userAppDataActions.subtractGems(currentModelPrice);
        console.log(`Deducted ${currentModelPrice} credits for image generation`);
      } catch (error) {
        console.error('Error deducting credits:', error);
        // Don't block the user if credit deduction fails
      }

      setLastGeneratedImage(data.image);

      // Call the callback if provided
      if (onImageGenerated) {
        onImageGenerated(data.image);
      }

      return {
        success: true,
        image: data.image,
        jobId: data.jobId,
        status: data.status,
        executionTime: data.executionTime,
        delayTime: data.delayTime
      };

    } catch (error) {
      console.error('Error generating image:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    } finally {
      setIsGenerating(false);
    }
  }, [userEmail, userAppDataState, onImageGenerated]);

  const resetState = useCallback(() => {
    setIsGenerating(false);
    setLastGeneratedImage(null);
  }, []);

  return {
    generateImage,
    isGenerating,
    lastGeneratedImage,
    resetState
  };
}

// Helper function to convert Cloudinary image to base64
async function getImageAsBase64(imageUrl: string): Promise<string | null> {
  try {
    const response = await fetch(imageUrl);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Error converting Cloudinary image to base64:', error);
    return null;
  }
}

/**
 * In-chat image generation state type
 */
export type InChatImageGenState = 'idle' | 'generating' | 'applying' | 'error';

interface UseInChatImageGenParams {
  avatarId: string;
  avatarName: string;
}

/**
 * Hook for automated in-chat image generation triggered by "show me" messages
 * Handles the entire flow: finding base image, generating, and applying to messages
 */
export function useInChatImageGen({ avatarId, avatarName }: UseInChatImageGenParams) {
  const { userEmail } = useAuth();
  const localeContext = useLocale();
  const locale: Locale = (localeContext?.locale === 'en' || localeContext?.locale === 'zh-Hans') 
    ? localeContext.locale 
    : 'en';
  const [state, setState] = useState<InChatImageGenState>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [globalChatStateData, globalChatStateActions] = useGlobalChatState();

  // Import required functions
  const { generateImage } = useImageGenAPI({});

  // Get LiveKit room state for image swap functionality
  const { globalLKRoomState } = useGlobalLiveKitRoomState();
  const room = globalLKRoomState.room;

  const triggerInChatImageGen = useCallback(async (prompt: string) => {
    if (!userEmail) {
      setState('error');
      setErrorMessage('Authentication required');
      return;
    }

    setState('generating');
    setErrorMessage(null);
    // Set global image generating state to disable chat controls
    globalChatStateActions.setIsImageGenerating(true);

    try {
      // Step 1: Get recent messages and last image message
      const { liveMessageDataHub } = await import('../data/liveMessageData/liveMessageDataHub');
      const messages = liveMessageDataHub.getMessages();
      const lastImageMessage = liveMessageDataHub.getLastMessageWithImage();

      if (!lastImageMessage || !lastImageMessage.imageUrl) {
        setState('error');
        setErrorMessage('No reference image found in chat');
        globalChatStateActions.setIsImageGenerating(false);
        return;
      }

      // Step 2: Get the last 2 chat messages (excluding image messages)
      const chatMessages = messages.filter(msg => !msg.imageUrl).slice(-4);
      
      // Step 3: Generate image edit prompt using the new API
      const imageEditPromptResponse = await fetch('/api/chat/generate_image_prompt', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          messages: chatMessages.map(msg => ({
            role: msg.role,
            content: msg.content,
          })),
          lastImageMessageContent: lastImageMessage.content,
          locale: locale,
        }),
      });

      if (!imageEditPromptResponse.ok) {
        globalChatStateActions.setIsImageGenerating(false);
        throw new Error('Failed to generate image edit prompt');
      }

      const imageEditPromptData = await imageEditPromptResponse.json();
      if (!imageEditPromptData.text) {
        globalChatStateActions.setIsImageGenerating(false);
        throw new Error('No image edit prompt generated');
      }

      // Step 4: Get the Cloudinary URL and convert to base64
      const imageWithStatus = lastImageMessage.imageWithStatus;
      if (!imageWithStatus?.imageData || typeof imageWithStatus.imageData.toURL !== 'function') {
        setState('error');
        setErrorMessage('Unable to load reference image');
        globalChatStateActions.setIsImageGenerating(false);
        return;
      }

      const imageUrl = imageWithStatus.imageData.toURL();
      const base64Image = await getImageAsBase64(imageUrl);

      if (!base64Image) {
        setState('error');
        setErrorMessage('Failed to convert reference image');
        globalChatStateActions.setIsImageGenerating(false);
        return;
      }

      // Step 5: Generate image using the API with the generated prompt
      const imageData = {
        filename: 'input_image.png',
        base64: base64Image,
        width: 768,
        height: 1344
      };

      const result = await generateImage(imageData, imageEditPromptData.text, 'in-chat show me');

      if (!result.success || !result.image) {
        setState('error');
        setErrorMessage(result.error || 'Failed to generate image');
        globalChatStateActions.setIsImageGenerating(false);
        return;
      }

      // Step 4: Upload and save to messages (applying state)
      setState('applying');

      // Convert base64 to blob for upload
      const response = await fetch(result.image);
      const blob = await response.blob();

      // Generate unique keys for S3 and Cloudinary
      const timestamp = Date.now();
      const randomId = Math.random().toString(36).substring(2, 8);
      const s3Key = `rita-swap-images/${userEmail}/${avatarId}/${timestamp}-${randomId}.jpg`;
      const cloudinaryKey = `rita-swap-images/${userEmail}/${avatarId}/${timestamp}-${randomId}`;

      // Run four operations concurrently: S3 upload, Cloudinary upload, image captioning, and presigned URL generation
      const [s3UploadResult, cloudinaryResult, captionResult, presignedUrlForSwap] = await Promise.all([
        // S3 upload (returns both upload response and presigned URL for reuse)
        (async () => {
          const { generatePresignedUrl } = await import('@/app/lib/actions/s3');
          const presignedUrlResponse = await generatePresignedUrl(s3Key);
          const s3UploadResponse = await fetch(presignedUrlResponse.presignedUrl, {
            method: 'PUT',
            body: blob,
            headers: {
              'Content-Type': 'image/jpeg',
            },
          });
          if (!s3UploadResponse.ok) {
            throw new Error('Failed to upload image to S3');
          }
          return { uploadResponse: s3UploadResponse, presignedUrl: presignedUrlResponse.presignedUrl };
        })(),
        
        // Cloudinary upload
        (async () => {
          const { uploadImageToCloudinary } = await import('@/app/lib/cloudinary/cloudinary');
          const file = new File([blob], 'generated-image.jpg', { type: 'image/jpeg' });
          const cloudinaryResult = await uploadImageToCloudinary(file, undefined, cloudinaryKey);
          if (!cloudinaryResult || !cloudinaryResult.public_id) {
            throw new Error('Failed to upload image to Cloudinary');
          }
          return cloudinaryResult;
        })(),
        
        // Image captioning
        (async () => {
          // Extract base64 data from the data URL
          const base64Data = result.image!.split(',')[1];
          const response = await fetch('/api/image_caption', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              imageData: base64Data,
              mimeType: 'image/jpeg',
              locale: locale,
            }),
          });
          if (!response.ok) {
            throw new Error(`Image caption API error: ${response.status}`);
          }
          return await response.json();
        })(),
        
        // Generate presigned URL for step 7 (reuse in image swap)
        (async () => {
          const presigned_url = await getPresignedUrl(s3Key);
          return presigned_url.presignedUrl;
        })()
      ]);

      // Create message content with caption and prompt (matching Python backend format)
      let messageContent = imageEditPromptData.text;
      if (captionResult.success && captionResult.caption) {
        messageContent = `Image description: ${captionResult.caption}\nImage edit prompt: ${imageEditPromptData.text}`;
      }

      // Add message to datahub with the S3 key as imageUrl
      // Use constructLiveMessage to properly create the message with automatic image loading
      const messageId = `img-${timestamp}-${randomId}`;
      const { constructLiveMessage } = await import('../data/liveMessageData/liveMessage');
      const liveMessage = constructLiveMessage({
        id: messageId,
        content: messageContent,
        role: 'assistant',
        sender_id: avatarId,
        sender_name: avatarName || 'Assistant',
        created_at: new Date().toISOString(),
        imageUrl: s3Key,
        isStreaming: false
      });
      
      await liveMessageDataHub.addMessage(liveMessage);

      console.log('Successfully applied in-chat generated image:', {
        s3Key,
        cloudinaryPublicId: cloudinaryResult.public_id,
        messageId
      });

      // step 6: set the active image to the newly generated one
      // This now automatically includes head detection and image swap for live chat
      await globalChatStateActions.setActiveImage(s3Key);
      
      // Success - return to idle after a brief delay to ensure message is added
      setTimeout(() => {
        setState('idle');
        setErrorMessage(null);
        globalChatStateActions.setIsImageGenerating(false);
      }, 100);


    } catch (error) {
      console.error('Error in in-chat image generation:', error);
      setState('error');
      setErrorMessage(error instanceof Error ? error.message : 'Unknown error occurred');
      globalChatStateActions.setIsImageGenerating(false);
    }
  }, [userEmail, avatarId, avatarName, generateImage, room, globalChatStateActions, locale]);

  const resetError = useCallback(() => {
    setState('idle');
    setErrorMessage(null);
  }, []);

  return {
    state,
    errorMessage,
    triggerInChatImageGen,
    resetError
  };
}
